// intro_video — a video plays, the learner taps continue. Presentational, so it
// fires onViewed on mount (the player enables Continue immediately; the kid can
// watch or skip). Captions render as a WebVTT <track> for a11y (CLAUDE.md §28).

import { useEffect, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { mediaUrl } from '../media';
import { ArabicText } from './primitives';
import type { ActivityRendererProps } from './types';

export function IntroVideo({
  data,
  ctx,
  cb,
}: ActivityRendererProps<content.IntroVideoData>): ReactElement {
  const { t } = ctx;
  useEffect(() => {
    cb.onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const src = mediaUrl(ctx.cdnBase, data.video);
  const poster = mediaUrl(ctx.cdnBase, data.poster);
  const captions = mediaUrl(ctx.cdnBase, data.captions);

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col items-center text-center">
      {data.titleKey ? (
        <h2 className="mb-5 text-2xl font-bold text-burgundy-700">
          <ArabicText text={t(data.titleKey)} ctx={ctx} />
        </h2>
      ) : null}
      <div className="w-full overflow-hidden rounded-3xl bg-ink-900 shadow-[0_24px_60px_-30px_rgba(31,26,23,0.4)] ring-1 ring-cream-200">
        {src ? (
          <video
            className="aspect-video w-full"
            src={src}
            poster={poster ?? undefined}
            controls
            playsInline
            preload="metadata"
          >
            {captions ? (
              <track
                kind="captions"
                src={captions}
                srcLang={ctx.isRtl ? 'ar' : 'en'}
                label={t('content.common.captions')}
                default
              />
            ) : null}
          </video>
        ) : (
          <div className="grid aspect-video w-full place-items-center text-6xl" aria-hidden="true">
            🎬
          </div>
        )}
      </div>
    </div>
  );
}
